import { useMemo } from 'react';
import { type Product } from '@/services/products/types';
import { type Offers, type ProductSchema, type UnitPriceSpecification } from './JsonLDMeta';

function sanitisePriceString(price: string): string {
  return price.replace(/[^0-9.]/g, '');
}

function getDiscountedPriceProperties(product: Product) {
  const priceValidUntil = product.discount?.validUntil;
  const isDiscounted =
    priceValidUntil && new Date() <= new Date(priceValidUntil.split('/').reverse().join('-'));
  const discountedPrice = product.discount?.pricePerUnit?.discountedPrice?.formatted;

  if (!isDiscounted || !discountedPrice) {
    return {};
  }

  const priceSpecification: UnitPriceSpecification = {
    '@type': 'UnitPriceSpecification',
    price: sanitisePriceString(product.formattedPrice),
    priceCurrency: 'GBP',
    priceType: 'https://schema.org/StrikethroughPrice',
  };

  return {
    price: sanitisePriceString(discountedPrice),
    priceSpecification,
    priceValidUntil,
  } as Partial<Offers>;
}

function buildOffersSchema(product: Product) {
  const availability = product.availability.isOnStock
    ? 'http://schema.org/InStock'
    : 'http://schema.org/OutOfStock';

  const offers: Offers = {
    '@type': 'Offer',
    availability,
    itemCondition: 'http://schema.org/NewCondition',
    price: sanitisePriceString(product.formattedPrice),
    priceCurrency: 'GBP',
    ...getDiscountedPriceProperties(product),
  };

  return offers;
}

function useProductSchema(product: Product): ProductSchema {
  const productSchema: ProductSchema = useMemo(() => {
    const ratingValue = product.rating?.averageRating.toString();
    const reviewCount = product.rating?.count.toString();

    return {
      '@type': 'Product',
      aggregateRating:
        ratingValue && reviewCount
          ? {
              '@type': 'AggregateRating',
              ratingValue,
              reviewCount,
            }
          : undefined,
      brand: {
        '@type': 'Brand',
        name: product.brand ?? '',
      },
      description: product.description ?? '',
      image: product.image?.src,
      name: product.name,
      offers: buildOffersSchema(product),
      seller: {
        '@type': 'Organization',
        name: 'Waitrose',
      },
    };
  }, [product]);

  return productSchema;
}

export default useProductSchema;
